import { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * NotificationContext — owns the user's persistent alert feed.
 *
 * Polls GET /api/notifications every 60s and exposes:
 *   notifications   — array of {notif_id, kind, title, body, ref_id, ref_type, status, created_at}
 *   unread          — count of status==="unread"
 *   active          — first not-dismissed alert for the AlertBar
 *   markRead(id)
 *   dismiss(id)
 *   markAllRead()
 *   refresh()       — force a fetch (call after creating events / follows)
 *
 * Driven by a single source of truth so the AlertBar (top of screen), the
 * future bell dropdown, and per-event indicators all stay in sync.
 */
const NotificationContext = createContext({
  notifications: [],
  unread: 0,
  active: null,
  markRead: () => {},
  dismiss: () => {},
  markAllRead: () => {},
  refresh: () => {},
});

const POLL_MS = 60_000;

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data?.items || []);
      setUnread(data?.unread || 0);
    } catch (e) {
      // Keep the last good list on transient errors so the bar doesn't flicker
      // — but log so we can spot a backend regression in dev.
      console.warn("[notifications] refresh failed:", e?.message || e);
    }
  }, [user?.user_id]);

  // Reset state when the signed-in user changes (login / logout) using the
  // derive-during-render pattern (avoids the set-state-in-effect lint).
  const [seenUid, setSeenUid] = useState(user?.user_id || null);
  if ((user?.user_id || null) !== seenUid) {
    setSeenUid(user?.user_id || null);
    setNotifications([]);
    setUnread(0);
  }

  useEffect(() => {
    if (!user?.user_id) return;
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [user?.user_id, refresh]);

  const markRead = useCallback(async (notif_id) => {
    setNotifications((prev) => prev.map((n) => n.notif_id === notif_id ? { ...n, status: "read" } : n));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${notif_id}/read`); }
    catch (e) { console.warn("[notifications] markRead failed:", e?.message || e); }
  }, []);

  const dismiss = useCallback(async (notif_id) => {
    setNotifications((prev) => prev.filter((n) => n.notif_id !== notif_id));
    try { await api.post(`/notifications/${notif_id}/dismiss`); }
    catch (e) { console.warn("[notifications] dismiss failed:", e?.message || e); }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); }
    catch (e) { console.warn("[notifications] markAllRead failed:", e?.message || e); }
  }, []);

  const active = notifications.find((n) => n.status !== "dismissed") || null;

  const value = useMemo(
    () => ({ notifications, unread, active, markRead, dismiss, markAllRead, refresh }),
    [notifications, unread, active, markRead, dismiss, markAllRead, refresh]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
